/**
 * Observation resolution — turning a raw observation into a position line.
 *
 * This is the layer between what the navigator actually does (takes a compass
 * bearing on a buoy, reads a radar range off a headland, lines up a transit)
 * and the pure geometry in fix.ts. It stays in `nav` and depends only on the
 * geodesy, so it is pure and unit-testable. It knows nothing about the
 * PlotObject graph; the chartwork layer maps objects onto these inputs.
 *
 * Bearing correction order is fixed and explicit, because getting it wrong is a
 * classic error: compass to magnetic by deviation, magnetic to true by
 * variation. East errors are positive. A relative bearing is measured clockwise
 * from the ship's head, so it needs the heading to resolve to true.
 */
import type { BearingLine, RangeCircle } from './fix';
import { normaliseBearing, rhumbBearingDeg } from './geodesy';

export interface BearingObsInput {
  bearing: number;
  bearingType: 'true' | 'magnetic' | 'compass' | 'relative';
  /** Magnetic to true. East positive. */
  variationDeg?: number;
  /** Compass to magnetic. East positive. */
  deviationDeg?: number;
  /** Ship's head in degrees true, needed to resolve a relative bearing. */
  shipHeadingDeg?: number;
}

/**
 * Reduce an observed bearing to degrees true.
 *
 *   compass  -> add deviation -> magnetic -> add variation -> true
 *   magnetic -> add variation -> true
 *   relative -> add ship's head -> true
 *   true     -> unchanged
 *
 * East errors are positive, which is the "compass to true, add east" rule.
 */
export function bearingToTrue(obs: BearingObsInput): number {
  const variation = obs.variationDeg ?? 0;
  const deviation = obs.deviationDeg ?? 0;
  switch (obs.bearingType) {
    case 'true':
      return normaliseBearing(obs.bearing);
    case 'magnetic':
      return normaliseBearing(obs.bearing + variation);
    case 'compass':
      return normaliseBearing(obs.bearing + deviation + variation);
    case 'relative':
      return normaliseBearing((obs.shipHeadingDeg ?? 0) + obs.bearing);
  }
}

/**
 * A bearing observation on a charted feature gives a line of position through
 * that feature. The line is infinite for intersection purposes (fix.ts treats a
 * BearingLine as a full line through `from`), so the direction sign does not
 * matter; we store the true bearing as observed.
 */
export function bearingObsToLine(obs: BearingObsInput, from: [number, number]): BearingLine {
  return { type: 'bearing', from, bearingDeg: bearingToTrue(obs) };
}

export type RangeUnit = 'nm' | 'cables' | 'm';

/** Convert a range to nautical miles. 1 cable = 0.1 nm, 1 nm = 1852 m. */
export function rangeToNm(range: number, unit: RangeUnit): number {
  switch (unit) {
    case 'nm':
      return range;
    case 'cables':
      return range * 0.1;
    case 'm':
      return range / 1852;
  }
}

/** A range observation on a charted feature gives a circle of position. */
export function rangeObsToCircle(
  range: number,
  unit: RangeUnit,
  centre: [number, number],
): RangeCircle {
  return { type: 'range', centre, rangeNm: rangeToNm(range, unit) };
}

/**
 * A transit: two charted marks in line. The line of position runs through both
 * marks, so its bearing is the bearing from the rear mark through the front
 * mark (the direction you look along the transit). This is the strongest visual
 * LOP because it needs no instrument, just the eye lining up two objects.
 */
export function transitToLine(
  front: [number, number],
  rear: [number, number],
): BearingLine {
  return { type: 'bearing', from: front, bearingDeg: rhumbBearingDeg(rear, front) };
}
